import type { CasOrCategory } from '../types/db.ts';
import type { ConcentrationBase } from './chemical-concentration-compute.ts';

export interface ChemicalWithSinglePercentage {
  casOrCategory: CasOrCategory;
  concentrationBase: ConcentrationBase;
}

interface DifferenceItem {
  casOrCategory: CasOrCategory;
  difference: number;
  left: number | null;
  right: number | null;
  isSimilar: boolean;
}

export function areChemicalCompositionsSimilar(
  composition1: ChemicalWithSinglePercentage[],
  composition2: ChemicalWithSinglePercentage[],
  tolerancePercent: number = 5,
): {
  isSimilar: boolean;
  differences: DifferenceItem[];
} {
  // If either composition is empty, they're not similar
  if (!composition1.length || !composition2.length) {
    return { isSimilar: false, differences: [] };
  }

  // If different number of chemicals, not similar
  if (composition1.length !== composition2.length) {
    return { isSimilar: false, differences: [] };
  }

  const chemicals = new Set<CasOrCategory>([
    ...composition1.map((chem) => chem.casOrCategory),
    ...composition2.map((chem) => chem.casOrCategory),
  ]);

  const leftConcentrations = new Map<CasOrCategory, number>(
    composition1.map((c) => [c.casOrCategory, c.concentrationBase]),
  );
  const rightConcentrations = new Map<CasOrCategory, number>(
    composition2.map((c) => [c.casOrCategory, c.concentrationBase]),
  );

  const differences: DifferenceItem[] = [];

  for (const casOrCategory of chemicals) {
    const left = leftConcentrations.get(casOrCategory) ?? null;
    const right = rightConcentrations.get(casOrCategory) ?? null;

    if (left === null || right === null) {
      differences.push({
        casOrCategory,
        difference: left ?? right ?? 0,
        left,
        right,
        isSimilar: false,
      });
      continue;
    }

    const difference = Math.abs(left - right);
    differences.push({
      casOrCategory,
      difference,
      left,
      right,
      isSimilar: difference <= (tolerancePercent / 100) * left || difference <= (tolerancePercent / 100) * right,
    });
  }

  const isSimilar = differences.every((d) => d.isSimilar);
  return { isSimilar, differences };
}
